































































































































































import { Component, Prop, Vue } from 'vue-property-decorator';
import InvoiceListDialogExtraInfo from './DialogExtraInfo.vue';
import { AppModule } from '@/store/modules/app';
import { search_invoices } from '@/api/invoice';
import { list_product_summaries } from '@/api/product';
import {
  CurrencyEnum,
  ProductTypeEnum,
  InvoiceOperationEnum,
} from '@/api/types';
import {
  formatTime,
  downloadFileFromBlob,
  stringFromIDate,
} from '@/utils/index';

type HeaderType = {
  text: string;
  value: string;
};

type WithIndex = {
  index: number;
};

@Component({
  name: 'InvoiceListResult',
  components: {
    InvoiceListDialogExtraInfo,
  },
})
export default class extends Vue {
  @Prop({ required: true }) products!: Map<
    string,
    list_product_summaries.Product
  >;
  @Prop({ required: true }) csvBlob!: string;
  @Prop({ required: true }) invoices!: search_invoices.InvoiceSummary[];
  private headers: HeaderType[] = [];
  private bookingOrdersHeaders: HeaderType[] = [];
  private agencyOrdersHeaders: HeaderType[] = [];
  private loading = false;
  private filterText = '';
  private allowDownload = false;
  private dialog = false;
  private currentExtraInfo: search_invoices.InvoiceSummary.ExtraInfo | null = null;

  get isDialogFullscreen() {
    return false && this.$vuetify.breakpoint.smAndDown;
  }

  get invoicesWithIndexAndUnOverPaid() {
    return this.invoices.map((v, i) =>
      Object.assign(
        {
          index: i + 1,
          unOverPaid: v.paidAmount - v.billingAmount,
        },
        v
      )
    );
  }

  get currency() {
    return CurrencyEnum.JPY;
  }

  get canDownload() {
    return this.allowDownload;
  }

  mounted() {
    this.loadData();
  }

  private async loadData() {
    this.loadHeaders();
    this.allowDownload = true;
  }

  private loadHeaders() {
    const index: HeaderType = {
      text: this.$t('Id') as string,
      value: 'index',
    };
    const generatedAt: HeaderType = {
      text: this.$t('Generated at') as string,
      value: 'generatedAt',
    };
    const division: HeaderType = {
      text: this.$t('Division') as string,
      value: 'division',
    };
    const agentName: HeaderType = {
      text: this.$t('Billing receiver') as string,
      value: 'agentName',
    };
    const status: HeaderType = {
      text: this.$t('Status') as string,
      value: 'status',
    };
    const billingAmount: HeaderType = {
      text: this.$t('Billing amount') as string,
      value: 'billingAmount',
    };
    const target: HeaderType = {
      text: this.$t('Billing target') as string,
      value: 'target',
    };
    const paidAmount: HeaderType = {
      text: this.$t('Paid amount') as string,
      value: 'paidAmount',
    };
    const unOverPaid: HeaderType = {
      text: `${this.$t('Unpaid')}(-)/${this.$t('Overpaid')}(+)`,
      value: 'unOverPaid',
    };
    const paidAt: HeaderType = {
      text: this.$t('Payment date') as string,
      value: 'paidAt',
    };
    const accountingSlipStatus: HeaderType = {
      text: this.$t('{msg} status', {
        msg: this.$t('Accounting slip'),
      }) as string,
      value: 'accountingSlipStatus',
    };
    const orders: HeaderType = {
      text: this.$t('Order info') as string,
      value: 'data-table-expand',
    };
    const extraInfo: HeaderType = {
      text: this.$t('Extra info') as string,
      value: 'extraInfo',
    };
    const headers: HeaderType[] = [
      index,
      generatedAt,
      division,
      agentName,
      status,
      billingAmount,
      target,
      paidAmount,
      unOverPaid,
      paidAt,
      accountingSlipStatus,
      orders,
      extraInfo,
    ];
    this.headers = headers;

    const productName: HeaderType = {
      text: this.$t('Product name') as string,
      value: 'productName',
    };
    const productType: HeaderType = {
      text: this.$t('Product type') as string,
      value: 'productType',
    };
    const soldAmount: HeaderType = {
      text: this.$t('Sold amount') as string,
      value: 'soldAmount',
    };
    const refundAmount: HeaderType = {
      text: this.$t('Refunded amount') as string,
      value: 'refundAmount',
    };
    this.bookingOrdersHeaders = [productName, productType, soldAmount];
    this.agencyOrdersHeaders = [
      productName,
      productType,
      soldAmount,
      refundAmount,
    ];
  }

  private async downloadCsv() {
    try {
      const filename = 'invoice.csv';
      this.allowDownload = false;
      AppModule.StartProgress();

      await downloadFileFromBlob(this.csvBlob || '', filename);

      this.$toast.success(
        this.$t('Downloaded {msg}', {
          msg: 'CSV',
        }) as string,
        {
          icon: 'mdi-file-download-outline',
        }
      );

      AppModule.StopProgress();
      this.allowDownload = true;
    } catch (err) {
      AppModule.StopProgress();
      this.allowDownload = true;
      this.$toast.error(
        this.$t('Failed to {do} due to {err}', {
          do: (this.$t(`Download`) as string).toLowerCase(),
          err: err,
        }) as string,
        {
          icon: 'mdi-alert',
        }
      );
    }
  }

  private getGeneratedAtText(generatedAt: string) {
    return formatTime(new Date(generatedAt));
  }

  private getTargetUrl(type: search_invoices.InvoiceTypeEnum, id: string) {
    return (type as search_invoices.InvoiceTypeEnum) ===
      search_invoices.InvoiceTypeEnum.AGENTCY_INVOICE
      ? `/invoice/agency/${id}`
      : (type as search_invoices.InvoiceTypeEnum) ===
        search_invoices.InvoiceTypeEnum.BOOKING_INVOICE
      ? `/booking/${id}`
      : '';
  }

  private getTargetText(type: search_invoices.InvoiceTypeEnum, id: string) {
    return (type as search_invoices.InvoiceTypeEnum) ===
      search_invoices.InvoiceTypeEnum.BOOKING_INVOICE
      ? `${this.$t('Booking')}: ${id}`
      : (type as search_invoices.InvoiceTypeEnum) ===
        search_invoices.InvoiceTypeEnum.AGENTCY_INVOICE
      ? `${this.$t('Invoice')}: ${id}`
      : '';
  }

  private getPaidAmountText(item: search_invoices.InvoiceSummary) {
    return item.paidAt
      ? `${item.paidAmount.toLocaleString()} ${this.currency}`
      : '';
  }

  private getPaidAtText(item: search_invoices.InvoiceSummary) {
    return item.paidAt
      ? stringFromIDate(item.paidAt)!
      : this.$t('PaymentStatusEnum.NOT_PAID');
  }

  private getAccountingSlipStatusText(item: search_invoices.InvoiceSummary) {
    return item.accountingSlipFinished
      ? this.$t('AccountingSlipStatusEnum.FINISHED')
      : this.$t('AccountingSlipStatusEnum.NOT_FINISHED');
  }

  private getItemClass(item: search_invoices.InvoiceSummary) {
    return item.isDelayed ? 'red--text font-weight-bold' : '';
  }

  private getOrdersHeaders(item: search_invoices.InvoiceSummary): HeaderType[] {
    return (item.invoiceType as search_invoices.InvoiceTypeEnum) ===
      search_invoices.InvoiceTypeEnum.BOOKING_INVOICE
      ? this.bookingOrdersHeaders
      : (item.invoiceType as search_invoices.InvoiceTypeEnum) ===
        search_invoices.InvoiceTypeEnum.AGENTCY_INVOICE
      ? this.agencyOrdersHeaders
      : [];
  }

  private getOrders(item: search_invoices.InvoiceSummary) {
    return item.orders.filter(
      o => (o.type as ProductTypeEnum) !== ProductTypeEnum.FREE_MATERIAL
    );
  }

  private getProductNameText(item: search_invoices.OrderInfo) {
    return `${
      this.$i18n.locale !== 'ja' &&
      this.products.has(item.productId) &&
      this.products.get(item.productId)!.nameEn
        ? this.products.get(item.productId)!.nameEn
        : item.productName
    }${
      this.products.get(item.productId)!.fiscalYear === 0
        ? ''
        : ' [' + this.products.get(item.productId)!.fiscalYear + ']'
    }`;
  }

  private canShowExtraInfo(item: search_invoices.InvoiceSummary) {
    return !!item.extraInfo && Object.keys(item.extraInfo).length > 0;
  }

  private openExtraInfoDialog(item: search_invoices.InvoiceSummary) {
    this.currentExtraInfo = item.extraInfo;
    this.$nextTick(() => {
      this.dialog = true;
    });
  }
}
